import './styles.scss';

import Input, { InputProps } from '@/components/Input';
import React, { ReactNode, useMemo } from 'react';

import { InputGroup } from 'react-bootstrap';
import { fieldWrapper } from '@/components/Field';

export type NumberInputProps = Omit<InputProps, 'value' | 'onChange'> & {
  value: number;
  onChange: (value: number) => void;
  //TODO: change styles to work without needing the append prop
  append?: React.ReactNode;
  min?: number;
  max?: number;
};
const NumberInput = ({ value, onChange, append, min, max, ...props }: NumberInputProps): ReactNode => {
  const parsedValue = useMemo((): number => (!isNaN(parseFloat(`${value}`)) ? parseFloat(`${value}`) : undefined), [value]);
  // TODO: wrap in useCallback and optimize
  const handleChange = (val: string, event?: React.SyntheticEvent): void => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    val.replace(/[^0-9.-]/g, '').replace(/([0-9.-]{1,})-/g, '$1');
    const result = val === '' ? undefined : !isNaN(parseFloat(val)) ? parseFloat(val) : parsedValue;
    if (min !== undefined && result < min) return onChange(min);
    if (max !== undefined && result > max) return onChange(max);
    onChange(result);
  };

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    // arrow keys
    if (event.key === 'ArrowUp') return handleChange(`${(parsedValue || 0) + 1}`, event);
    if (event.key === 'ArrowDown') return handleChange(`${(parsedValue || 0) - 1}`, event);
  };

  return (
    <InputGroup className="NumberInput">
      <Input onKeyDown={handleKeyDown} {...props} value={parsedValue ?? ''} onChange={handleChange} />
      <div className="NumberInput-Controls">
        <button onClick={(event): void => handleChange(`${(parsedValue || 0) + 1}`, event)} tabIndex={-1} disabled={props.disabled}>
          <i className="fa fa-angle-up" />
        </button>
        <button onClick={(event): void => handleChange(`${(parsedValue || 0) - 1}`, event)} tabIndex={-1} disabled={props.disabled}>
          <i className="fa fa-angle-down" />
        </button>
      </div>
      {append && <div className="NumberInput-Append">{append}</div>}
    </InputGroup>
  );
};

export const NumberInputField = fieldWrapper<NumberInputProps>(NumberInput);

const MAX_LENGTH = 12;
const CurrencyInput = ({ value, onChange, ...props }: NumberInputProps) => {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Allow navigation keys: Arrow keys, Home, End, Tab
    if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Tab'].includes(event.key)) return;

    event.preventDefault();

    if (event.key === 'Delete') return onChange(0);
    if (event.key === 'Backspace') return onChange(Math.max(Math.floor(value / 10) / 100, 0));
    if (`${value}`.length >= (props?.max || MAX_LENGTH)) return;
    if (event.key >= '0' && event.key <= '9') return onChange(Math.max((value * 10 + parseInt(event.key)) / 100, 0));
  };

  const formatCurrency = (amountInCents: number) => {
    const centsStr: string = amountInCents.toString();

    if (centsStr.length <= 2) return '0.' + centsStr.padStart(2, '0');
    const cents: string = centsStr.slice(-2);
    const dollars: string = centsStr.slice(0, -2);
    const formattedDollars: string = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return formattedDollars + '.' + cents;
  };

  return <Input type="text" value={formatCurrency(value)} onChange={() => null} onKeyDown={handleKeyDown} {...props} />;
};

const CurrencyInputField = fieldWrapper<NumberInputProps>(CurrencyInput);

NumberInput.Currency = CurrencyInput;
NumberInputField['Currency'] = CurrencyInputField;

export default NumberInput;
